import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { clearSession, emailReport, getSession, getStatus } from "./api";
import logo from "../../assets/maksu-logo.jpg";
import "./idv.css";

const DOC_LABELS = {
  PASSPORT: "Passport",
  ID_CARD: "ID card",
  DRIVERS_LICENSE: "Driver's licence",
  RESIDENCE_PERMIT: "Residence permit",
};

function docLabel(type) {
  if (!type) return type;
  const key = String(type).toUpperCase();
  return DOC_LABELS[key] || key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, " ");
}

// Used for both /id-verification/success and /id-verification/failure.
export default function ResultPage({ type }) {
  const location = useLocation();
  const [data, setData] = useState(location.state);
  const [loading, setLoading] = useState(!location.state);
  const emailed = useRef(false);

  // If the page was refreshed, the response is gone from router state — fetch it
  // again with the ids in this browser's storage.
  useEffect(() => {
    if (data) return;
    const session = getSession();
    if (!session?.sessionId) {
      setLoading(false);
      return;
    }
    getStatus(session)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [data]);

  // Email the PDF report once per finished verification.
  useEffect(() => {
    if (!data?.status || emailed.current) return;
    const session = getSession();
    if (!session?.sessionId) return;
    emailed.current = true;
    emailReport(session).catch(() => {});   // best effort — never block the result
  }, [data]);

  if (!data) {
    return (
      <div className="idv-page">
        <div className="idv-card idv-center">
          <img src={logo} alt="MAKSU" className="idv-logo" />
          {loading ? (
            <div className="idv-spinner" aria-hidden="true" />
          ) : (
            <>
              <h1>Result not available here</h1>
              <p className="idv-text">
                Open this page on the device where you started the verification.
              </p>
              <Link to="/id-verification" className="idv-button">Start a verification</Link>
            </>
          )}
        </div>
      </div>
    );
  }

  const success = type === "success";
  const reasonCode = data?.reason_code ?? data?.reasonCode;
  const name = [data?.person_first_name, data?.person_last_name].filter(Boolean).join(" ");

  const details = [
    ["Status", data?.status],
    ["Name", name],
    ["Document", docLabel(data?.document_type)],
    ["Country", data?.document_country],
    ["Document number", data?.document_number],
    ["Reason", data?.reason && `${data.reason}${reasonCode ? ` (code ${reasonCode})` : ""}`],
    ["Decision time", data?.decision_time && new Date(data.decision_time).toLocaleString()],
  ].filter(([, value]) => value);

  return (
    <div className="idv-page">
      <div className="idv-card idv-card--result">
        <div className={`idv-result-band ${success ? "is-success" : "is-failure"}`}>
          <span className="idv-result-mark" aria-hidden="true">{success ? "✓" : "✕"}</span>
          <div className="idv-result-headline">
            <h1>{success ? "Identity verified" : "Verification failed"}</h1>
            {name && <p>{name}</p>}
          </div>
          <p className="idv-result-brand">
            MAKSU <span>(UTek)</span>
          </p>
        </div>

        <div className="idv-result-body">
        {details.length > 0 && (
          <dl className="idv-details">
            {details.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}

        <Link to="/id-verification" className="idv-button" onClick={clearSession}>
          Back to home
        </Link>
        </div>
      </div>
    </div>
  );
}